import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.adminPeople)

export const LEAVE_INSTANCE = service.instanceKey

export const LEAVE_ENDPOINTS = {
  // Concurrent absence for a request (TO-02).
  VIEW_LEAVE_OVERLAP: "/admin/requests/view/leaveoverlap",
  VIEW_OVERLAP_SETTINGS: "/admin/requests/view/leaveoverlapsettings",
  VIEW_LEAVE_CALENDAR: "/admin/requests/view/leavecalendar",
  VIEW_PENDING_LEAVE_REPORT: "/admin/requests/view/pendingleavereport",
  VIEW_LEAVE_TAKEN_REPORT: "/admin/requests/view/leavetakenreport",
  VIEW_LEAVE_BY_TYPE_REPORT: "/admin/requests/view/leavebytypereport",
  VIEW_LEAVE_BY_DIVISION_REPORT: "/admin/requests/view/leavebydivisionreport",
  VIEW_LEAVE_DEPARTMENTS: "/admin/requests/view/leavedepartments",
  VIEW_LEAVE_APPROVAL_HISTORY_REPORT:
    "/admin/requests/view/leaveapprovalhistoryreport",
  VIEW_LEAVE_PLANNING_REPORT: "/admin/requests/view/leaveplanningreport",
  VIEW_LEAVE_REPORT_VIEWS: "/admin/requests/view/leavereportviews",
  SAVE_LEAVE_REPORT_VIEW: "/admin/requests/save_leavereportview",
  DELETE_LEAVE_REPORT_VIEW: "/admin/requests/delete_leavereportview",
  VIEW_APPROVAL_CHAIN: "/admin/requests/view/approvalchain",
  ESS_VIEW_APPROVAL_CHAIN: "/employee/requests/view/approvalchain",
  VIEW_PLANNING_CYCLE: "/admin/requests/view/leaveplanningcycle",
  VIEW_PLANNING_CYCLES: "/admin/requests/view/leaveplanningcycles",
  SELECT_LEAVE_CANCELLATIONS: "/admin/requests/selectleavecancellations",
  VIEW_LEAVE_REQUEST_SETTINGS: "/admin/requests/view/leaverequestsettings",
  SAVE_LEAVE_REQUEST_SETTINGS: "/admin/requests/save_leaverequestsettings",
  OPEN_PLANNING_CYCLE: "/admin/requests/leaveplanning/open_cycle",
  CLOSE_PLANNING_CYCLE: "/admin/requests/leaveplanning/close_cycle",
  VIEW_PLANNING_ROSTER: "/admin/requests/view/leaveplanningroster",
  PLANNING_REQUEST_REVISION: "/admin/requests/leaveplanning/request_revision",
  PLANNING_MARK_COORDINATED: "/admin/requests/leaveplanning/mark_coordinated",
  SAVE_OVERLAP_SETTINGS: "/admin/requests/save_leaveoverlapsettings",

  // Leave lifecycle, administrative.
  WITHDRAW_LEAVE_REQUEST: "/admin/requests/withdraw_leaverequest",
  CANCEL_LEAVE_REQUEST: "/admin/requests/cancel_leaverequest",
  MODIFY_LEAVE_REQUEST: "/admin/requests/modify_leaverequest",
  EXTEND_LEAVE_REQUEST: "/admin/requests/extend_leaverequest",

  // Annual leave planning, self-service (AL-02, AL-04, AL-07).
  ESS_PLANNING_CYCLE: "/employee/requests/leaveplanning/open_cycle",
  ESS_PLANNING_MY_PERIODS: "/employee/requests/leaveplanning/my_periods",
  ESS_PLANNING_GUIDANCE: "/employee/requests/leaveplanning/guidance",
  ESS_PLANNING_ADD_PERIOD: "/employee/requests/leaveplanning/add_period",
  ESS_PLANNING_UPDATE_PERIOD: "/employee/requests/leaveplanning/update_period",
  ESS_PLANNING_SUBMIT_PERIOD: "/employee/requests/leaveplanning/submit_period",
  ESS_PLANNING_WITHDRAW_PERIOD:
    "/employee/requests/leaveplanning/withdraw_period",
  ESS_PLANNING_CONVERT_PERIOD:
    "/employee/requests/leaveplanning/convert_period",

  // Leave lifecycle, self-service.
  ESS_WITHDRAW_LEAVE_REQUEST: "/employee/requests/withdraw_leaverequest",
  ESS_CANCEL_LEAVE_REQUEST: "/employee/requests/cancel_leaverequest",
  ESS_MODIFY_LEAVE_REQUEST: "/employee/requests/modify_leaverequest",
  ESS_EXTEND_LEAVE_REQUEST: "/employee/requests/extend_leaverequest",
} as const

/** Mirrors OverlappingAbsenceViewModel. Carries no balance and no reason, by design (LC-04). */
export interface IOverlappingAbsence {
  employee_request_id: number
  person_id: number | null
  full_name: string | null
  first_day: string | null
  last_day: string | null
  status: string | null
  is_pending: boolean
}

export interface IConcurrentAbsenceDay {
  date: string | null
  absent_count: number
  exceeds_threshold: boolean
}

export interface ILeaveOverlap {
  department_id: number | null
  department: string | null
  threshold: number | null
  threshold_configured: boolean
  is_in_conflict: boolean
  days: IConcurrentAbsenceDay[]
  overlapping_absences: IOverlappingAbsence[]
}

export interface IDepartmentOverlapThreshold {
  department_id: number
  department: string | null
  threshold: number | null
  effective_threshold: number | null
  employee_count: number
}

export interface ILeaveOverlapSettings {
  default_threshold: number | null
  departments: IDepartmentOverlapThreshold[]
}

export type LeaveCalendarScope =
  | "mine"
  | "team"
  | "department"
  | "workplace"
  | "company"

export interface ILeaveCalendarEntry {
  employee_request_id: number
  person_id: number | null
  full_name: string | null
  department_id: number | null
  department: string | null
  first_day: string | null
  last_day: string | null
  kind: "approved" | "pending" | "planned"
  leave_policy: string | null
  label: string | null
}

export interface ILeaveCalendarDay {
  date: string | null
  is_non_working: boolean
  holiday: string | null
}

export interface ILeaveCalendar {
  scope: LeaveCalendarScope
  first_day: string | null
  last_day: string | null
  available_scopes: LeaveCalendarScope[]
  can_see_leave_types: boolean
  entries: ILeaveCalendarEntry[]
  days: ILeaveCalendarDay[]
}

/** Mirrors LeavePlanningCycleViewModel. */
/** What may be done to a leave request now, as decided by the server. */
export interface ILeaveRequestActions {
  can_withdraw: boolean
  can_cancel: boolean
  can_edit: boolean
  can_extend: boolean
  /** "Cancellation", "Modification" or "Extension" while one awaits approval. */
  pending_change: string | null
  /** For leave cancelled part way. */
  days_used: number | null
  days_booked: number | null
}

export interface ILeaveChange {
  employee_request_id: number
  leave_request_id: number | null
  /** 2 cancellation, 3 change, 4 extension. */
  request_kind_id: number | null
  request_kind: string | null
  person_key: string
  full_name: string
  avatar_url: string | null
  department: string | null
  leave: string | null
  leave_first_day: string | null
  leave_last_day: string | null
  days_booked: number | null
  last_day_taken: string | null
  requested_first_day: string | null
  requested_last_day: string | null
  days_returned: number | null
  /** Days returned on a cancellation, added on an extension, moved on a change. */
  days_affected: number | null
  reason: string | null
  raised_by: string | null
  employee_request_status_id: number
  employee_request_status: string | null
  review_status: string | null
  requested_on: string | null
}

export interface ILeaveRequestSettings {
  admin_cancellation_requires_approval: boolean
}

export interface ILeavePlanningCycle {
  leave_planning_cycle_id: number
  planning_year: number
  opens_on: string | null
  submission_deadline: string | null
  closed_on: string | null
  note: string | null
  accepts_entries: boolean
  is_closed: boolean
}

/**
 * Mirrors LeavePlanningPeriodViewModel. A tentative period, never a booking — nothing here
 * has touched a balance, which is why the roster shows indicative days rather than a
 * deduction.
 */
export interface ILeavePlanningPeriod {
  leave_planning_period_id: number
  leave_planning_cycle_id: number | null
  person_id: number | null
  full_name: string | null
  department_id: number | null
  department: string | null
  leave_policy_id: number | null
  leave_policy: string | null
  first_day: string | null
  last_day: string | null
  indicative_days: number | null
  status_id: number | null
  status: string | null
  note: string | null
  review_note: string | null
  converted_employee_request_id: number | null
}

/**
 * Mirrors LeavePlanningRosterViewModel. `days` carries only the days that breach, counted
 * against the same threshold the approval screen uses (TO-06).
 */
export interface ILeavePlanningRoster {
  department_id: number | null
  department: string | null
  threshold: number | null
  threshold_configured: boolean
  days: IConcurrentAbsenceDay[]
  periods: ILeavePlanningPeriod[]
}

/**
 * Mirrors LeavePlanningGuidanceViewModel (AL-04). Entitlement and remainder are null when the
 * policy carries none for this employee: unknown, not zero, so nothing is warned about.
 */
export interface ILeavePlanningGuidance {
  leave_policy_id: number
  leave_policy: string | null
  projected_entitlement_days: number | null
  planned_days: number
  remaining_days: number | null
  exceeds_entitlement: boolean
  warning: string | null
}

/** Mirrors PendingLeaveReportRow (RP-03). */
export interface IPendingLeaveRow {
  employee_request_id: number
  person_id: number | null
  full_name: string | null
  department: string | null
  leave_policy: string | null
  first_day: string | null
  last_day: string | null
  days: number | null
  submitted_on: string | null
  days_since_submitted: number
  waiting_since: string | null
  days_with_current_holder: number
  current_holder: string | null
  current_holder_kind: "person" | "role" | "unassigned" | null
  review_steps_done: number | null
  workflow_steps: number | null
}

export interface IPendingLeaveReport {
  total_pending: number
  unassigned_count: number
  oldest_days_waiting: number
  rows: IPendingLeaveRow[]
}

/** Mirrors ApprovalChainStepViewModel (AP-06). */
export interface IApprovalChainStep {
  position: number
  approver: string | null
  state: "approved" | "declined" | "waiting" | "upcoming" | null
  actioned_by: string | null
  actioned_on: string | null
  comment: string | null
  /** The step names a role that resolves to nobody, so the request cannot move. */
  is_unresolved: boolean
}

export interface IApprovalChain {
  employee_request_id: number
  status: string | null
  steps_completed: number
  total_steps: number
  current_holder: string | null
  steps: IApprovalChainStep[]
}

/** Mirrors LeavePlanningPeriodStatuParams. */
export const PLANNING_STATUS = {
  DRAFT: 1,
  SUBMITTED: 2,
  REVISION_REQUESTED: 3,
  COORDINATED: 4,
  CONVERTED: 5,
  WITHDRAWN: 6,
} as const

/**
 * RP. Filters the four analytics reports share. Days are attributed to a request's start
 * date, so the same range means the same thing in every one of them and the totals reconcile.
 */
export interface ILeaveReportFilter {
  first_day?: string
  last_day?: string
  department_id?: number
  leave_policy_id?: number
}

export interface ILeaveTakenRow {
  person_id: number | null
  full_name: string | null
  department: string | null
  requests: number
  days_taken: number
  paid_days: number
  unpaid_days: number
}

export interface ILeaveTakenReport {
  first_day: string | null
  last_day: string | null
  total_days: number
  total_paid_days: number
  total_unpaid_days: number
  rows: ILeaveTakenRow[]
}

export interface ILeaveByTypeRow {
  leave_policy_id: number | null
  leave_policy: string | null
  is_paid: boolean | null
  requests: number
  employees: number
  days_taken: number
  average_duration: number
}

export interface ILeaveByTypeReport {
  first_day: string | null
  last_day: string | null
  total_days: number
  rows: ILeaveByTypeRow[]
}

export interface ILeaveDepartmentOption {
  departmentId: number
  description: string
}

export interface ILeaveByDivisionRow {
  department_id: number | null
  department: string | null
  employees_absent: number
  requests: number
  days_taken: number
  peak_concurrent_absence: number
  peak_day: string | null
  absence_rate: number
}

export interface ILeaveByDivisionReport {
  first_day: string | null
  last_day: string | null
  total_days: number
  rows: ILeaveByDivisionRow[]
}

export interface IApprovalHistoryStep {
  position: number
  decision: string | null
  decided_by: string | null
  decided_on: string | null
  comment: string | null
}

export interface IApprovalHistoryRow {
  employee_request_id: number
  full_name: string | null
  department: string | null
  leave_policy: string | null
  first_day: string | null
  last_day: string | null
  days: number | null
  status: string | null
  submitted_on: string | null
  steps_recorded: number
  /** Null where the workflow cannot say how long the chain is for this request. */
  workflow_steps: number | null
  days_to_decide: number
  steps: IApprovalHistoryStep[]
}

/** RP-01. A saved way of looking at one report: range, filters and visible columns. */
export interface ISavedLeaveReportView {
  leave_report_view_id: number
  report_key: string | null
  description: string | null
  /** JSON the screen wrote and only the screen reads. */
  definition: string | null
  updated_on: string | null
}

export interface IApprovalHistoryReport {
  first_day: string | null
  last_day: string | null
  total_requests: number
  undecided_requests: number
  rows: IApprovalHistoryRow[]
}

export const leaveService = {
  ...service,

  getLeaveOverlap: (employeeRequestId: number) =>
    service.get<{ data: ILeaveOverlap; message?: string }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_OVERLAP,
      { employee_request_id: employeeRequestId }
    ),

  getLeaveCalendar: (params: {
    scope: LeaveCalendarScope
    first_day: string
    last_day: string
    /** Comma-separated. Only read for the department scope. */
    department_ids?: string
    /** Comma-separated. Only read for the workplace scope. */
    workplace_ids?: string
    leave_policy_id?: number
  }) =>
    service.get<{ data: ILeaveCalendar }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_CALENDAR,
      params
    ),

  // RP. The four analytics reports take the same filter, so one helper shape covers them all
  // and a range chosen on screen carries across every tab unchanged.
  getLeaveTakenReport: (params: ILeaveReportFilter) =>
    service.get<{ data: ILeaveTakenReport }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_TAKEN_REPORT,
      params
    ),

  getLeaveByTypeReport: (params: ILeaveReportFilter) =>
    service.get<{ data: ILeaveByTypeReport }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_BY_TYPE_REPORT,
      params
    ),

  /**
   * The departments the current sub-account's people are in.
   *
   * The company-wide list also carries departments belonging to no sub-account, so a picker
   * built from it offered departments the report could never show.
   */
  getDepartmentOptions: () =>
    service.get<{ data: ILeaveDepartmentOption[] }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_DEPARTMENTS
    ),

  getLeaveByDivisionReport: (params: ILeaveReportFilter) =>
    service.get<{ data: ILeaveByDivisionReport }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_BY_DIVISION_REPORT,
      params
    ),

  getLeaveApprovalHistoryReport: (params: ILeaveReportFilter) =>
    service.get<{ data: IApprovalHistoryReport }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_APPROVAL_HISTORY_REPORT,
      params
    ),

  getSavedReportViews: (reportKey?: string) =>
    service.get<{ data: ISavedLeaveReportView[] }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_REPORT_VIEWS,
      reportKey ? { report_key: reportKey } : {}
    ),

  saveReportView: (payload: {
    report_key: string
    description: string
    definition: string
  }) => service.post(LEAVE_ENDPOINTS.SAVE_LEAVE_REPORT_VIEW, payload),

  deleteReportView: (id: number) =>
    service.delete(`${LEAVE_ENDPOINTS.DELETE_LEAVE_REPORT_VIEW}?id=${id}`),

  getPendingLeaveReport: (params?: {
    department_id?: number
    minimum_days_waiting?: number
  }) =>
    service.get<{ data: IPendingLeaveReport }>(
      LEAVE_ENDPOINTS.VIEW_PENDING_LEAVE_REPORT,
      params ?? {}
    ),

  /** AP-06. selfService routes to the endpoint that refuses requests the caller does not own. */
  getApprovalChain: (employeeRequestId: number, selfService = false) =>
    service.get<{ data: IApprovalChain }>(
      selfService
        ? LEAVE_ENDPOINTS.ESS_VIEW_APPROVAL_CHAIN
        : LEAVE_ENDPOINTS.VIEW_APPROVAL_CHAIN,
      { employee_request_id: employeeRequestId }
    ),

  getPlanningCycle: () =>
    service.get<{ data: ILeavePlanningCycle | null; message?: string }>(
      LEAVE_ENDPOINTS.VIEW_PLANNING_CYCLE
    ),

  // Every cycle the company has, newest year first: upcoming, open and closed.
  getPlanningCycles: () =>
    service.get<{ data: ILeavePlanningCycle[]; message?: string }>(
      LEAVE_ENDPOINTS.VIEW_PLANNING_CYCLES
    ),

  /** Dates as DD/MM/YYYY. opens_on defaults to today on the server. */
  openPlanningCycle: (payload: {
    planning_year: string
    opens_on?: string
    submission_deadline?: string
    note?: string
  }) => service.post(LEAVE_ENDPOINTS.OPEN_PLANNING_CYCLE, payload),

  closePlanningCycle: (cycleId: number) =>
    service.post(`${LEAVE_ENDPOINTS.CLOSE_PLANNING_CYCLE}?id=${cycleId}`),

  // No departments means every department, for a caller permitted to see them all.
  getPlanningRoster: (cycleId: number, departmentIds: number[] = []) =>
    service.get<{ data: ILeavePlanningRoster }>(
      LEAVE_ENDPOINTS.VIEW_PLANNING_ROSTER,
      {
        cycle_id: cycleId,
        department_ids: departmentIds.length
          ? departmentIds.join(",")
          : undefined,
      }
    ),

  // Self-service planning. No person id is ever sent: the server resolves the signed-in
  // employee and refuses periods that are not theirs.
  getMyPlanningCycle: () =>
    service.get<{ data: ILeavePlanningCycle | null; message?: string }>(
      LEAVE_ENDPOINTS.ESS_PLANNING_CYCLE
    ),

  getMyPlanningPeriods: (cycleId: number) =>
    service.get<{ data: ILeavePlanningPeriod[] }>(
      LEAVE_ENDPOINTS.ESS_PLANNING_MY_PERIODS,
      { cycle_id: cycleId }
    ),

  getMyPlanningGuidance: (cycleId: number) =>
    service.get<{ data: ILeavePlanningGuidance[] }>(
      LEAVE_ENDPOINTS.ESS_PLANNING_GUIDANCE,
      { cycle_id: cycleId }
    ),

  /**
   * `save_as_draft` keeps the period private until it is submitted. Sketching a year of dates
   * takes more than one sitting, and submitting puts them on the departmental roster where a
   * supervisor may coordinate around them.
   */
  addPlanningPeriod: (
    cycleId: number,
    payload: {
      leave_policy_id: string
      first_day: string
      last_day: string
      note?: string
      save_as_draft?: boolean
    }
  ) =>
    service.post(
      `${LEAVE_ENDPOINTS.ESS_PLANNING_ADD_PERIOD}?cycle_id=${cycleId}`,
      payload
    ),

  updatePlanningPeriod: (
    periodId: number,
    payload: {
      leave_policy_id: string
      first_day: string
      last_day: string
      note?: string
      /** Pass false to save the edit and submit the draft in one go. */
      save_as_draft?: boolean
    }
  ) =>
    service.post(
      `${LEAVE_ENDPOINTS.ESS_PLANNING_UPDATE_PERIOD}?id=${periodId}`,
      payload
    ),

  /** Publishes a draft to the departmental roster. */
  submitPlanningPeriod: (periodId: number) =>
    service.post(
      `${LEAVE_ENDPOINTS.ESS_PLANNING_SUBMIT_PERIOD}?id=${periodId}`
    ),

  withdrawPlanningPeriod: (periodId: number) =>
    service.post(
      `${LEAVE_ENDPOINTS.ESS_PLANNING_WITHDRAW_PERIOD}?id=${periodId}`
    ),

  convertPlanningPeriod: (periodId: number, relievePersonId?: string) =>
    service.post(
      `${LEAVE_ENDPOINTS.ESS_PLANNING_CONVERT_PERIOD}?id=${periodId}${
        relievePersonId ? `&relieve_person_id=${relievePersonId}` : ""
      }`
    ),

  requestPlanningRevision: (periodId: number, comment: string) =>
    service.post(
      `${LEAVE_ENDPOINTS.PLANNING_REQUEST_REVISION}?id=${periodId}`,
      { comment }
    ),

  markPlanningCoordinated: (periodId: number) =>
    service.post(`${LEAVE_ENDPOINTS.PLANNING_MARK_COORDINATED}?id=${periodId}`),

  getOverlapSettings: () =>
    service.get<{ data: ILeaveOverlapSettings; message?: string }>(
      LEAVE_ENDPOINTS.VIEW_OVERLAP_SETTINGS
    ),

  saveOverlapSettings: (payload: {
    default_threshold: number | null
    departments: { department_id: number; threshold: number | null }[]
  }) => service.post(LEAVE_ENDPOINTS.SAVE_OVERLAP_SETTINGS, payload),

  /** lastDay is DD/MM/YYYY: the last day of leave taken. Today when left out. */
  cancelLeaveRequest: (
    employeeRequestId: number,
    reason: string,
    selfService = false,
    lastDay?: string
  ) =>
    service.post(
      `${
        selfService
          ? LEAVE_ENDPOINTS.ESS_CANCEL_LEAVE_REQUEST
          : LEAVE_ENDPOINTS.CANCEL_LEAVE_REQUEST
      }?id=${employeeRequestId}`,
      { reason, last_day: lastDay }
    ),

  getLeaveChanges: (statusId: number, params?: Record<string, any>) =>
    service.get<{ data: IPaginatedRes<ILeaveChange>["data"] }>(
      LEAVE_ENDPOINTS.SELECT_LEAVE_CANCELLATIONS,
      { statusId, ...params }
    ),

  getLeaveRequestSettings: () =>
    service.get<{ data: ILeaveRequestSettings }>(
      LEAVE_ENDPOINTS.VIEW_LEAVE_REQUEST_SETTINGS
    ),

  saveLeaveRequestSettings: (payload: ILeaveRequestSettings) =>
    service.post(LEAVE_ENDPOINTS.SAVE_LEAVE_REQUEST_SETTINGS, payload),

  withdrawLeaveRequest: (
    employeeRequestId: number,
    reason: string,
    selfService = false
  ) =>
    service.post(
      `${
        selfService
          ? LEAVE_ENDPOINTS.ESS_WITHDRAW_LEAVE_REQUEST
          : LEAVE_ENDPOINTS.WITHDRAW_LEAVE_REQUEST
      }?id=${employeeRequestId}`,
      { reason }
    ),

  extendLeaveRequest: (
    employeeRequestId: number,
    lastDay: string,
    reason: string,
    selfService = false
  ) =>
    service.post(
      `${
        selfService
          ? LEAVE_ENDPOINTS.ESS_EXTEND_LEAVE_REQUEST
          : LEAVE_ENDPOINTS.EXTEND_LEAVE_REQUEST
      }?id=${employeeRequestId}`,
      { last_day: lastDay, reason }
    ),

  modifyLeaveRequest: (
    employeeRequestId: number,
    payload: {
      first_day: string
      last_day: string
      leave_policy_id?: string
      reason?: string
    },
    selfService = false
  ) =>
    service.post(
      `${
        selfService
          ? LEAVE_ENDPOINTS.ESS_MODIFY_LEAVE_REQUEST
          : LEAVE_ENDPOINTS.MODIFY_LEAVE_REQUEST
      }?id=${employeeRequestId}`,
      payload
    ),
}
